import { Icon, type IconName } from '@/design-system/ui/Icon';
import { cn } from '@/design-system/ui/cn';
import type { HistoryStep } from '../model/schemas';

const ICONS: IconName[] = ['doc', 'user', 'box', 'home'];
const STEP_MS = 110;

/**
 * Vertical progress of a load: registered → driver assigned → picked up → delivered.
 * On first show each step rises in turn and the line between completed steps fills downward,
 * so the eye travels to where the load currently is. The current step also has a soft pulse.
 */
export function StatusTimeline({ history }: { history: HistoryStep[] }) {
  const currentIndex = history.reduce((acc, s, i) => (s.done ? i : acc), -1);
  return (
    <ol aria-label="روند ارسال مرسوله">
      {history.map((step, i) => {
        const last = i === history.length - 1;
        const cancelled = step.label === 'سفارش لغو شد';
        const current = i === currentIndex && !cancelled && !last;
        const lineFilled = step.done && history[i + 1]?.done;
        return (
          <li key={step.label} className="flex animate-rise gap-4" style={{ animationDelay: `${i * STEP_MS}ms` }} aria-current={step.done && (last || !history[i + 1]?.done) ? 'step' : undefined}>
            <div className="flex flex-col items-center">
              <span className="relative grid size-10 shrink-0 place-items-center">
                {current && <span className="absolute inset-0 animate-ping rounded-full bg-turquoise/30 [animation-duration:2s]" aria-hidden />}
                <span
                  className={cn(
                    'relative grid size-10 place-items-center rounded-full border-2',
                    cancelled ? 'border-danger bg-danger text-white' : step.done ? 'border-turquoise bg-cta text-white shadow-glow' : 'border-line bg-fill text-ink-3',
                  )}
                >
                  <Icon name={cancelled ? 'close' : (ICONS[i] ?? 'box')} size={18} />
                </span>
              </span>
              {!last && (
                <span className="relative my-1 min-h-6 w-0.5 flex-1 overflow-hidden rounded-full bg-line" aria-hidden>
                  {lineFilled && <span className="absolute inset-0 origin-top animate-grow-y rounded-full bg-gradient-to-b from-turquoise to-success" style={{ animationDelay: `${i * STEP_MS + 200}ms` }} />}
                </span>
              )}
            </div>
            <p className={cn('pt-2 text-sm', last ? 'pb-0' : 'pb-4', cancelled ? 'font-bold text-danger' : step.done ? 'font-bold text-navy' : 'text-ink-2')}>
              {step.label}
              <span className="sr-only">{step.done ? ' — انجام شد' : ' — در انتظار'}</span>
            </p>
          </li>
        );
      })}
    </ol>
  );
}
